import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export interface EmptyStateAction {
  label: ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'outline' | 'ghost' | 'default';
}

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: PhxIconName;
  title: ReactNode;
  description?: ReactNode;
  action?: EmptyStateAction;
}

export function EmptyState({
  icon = 'folder',
  title,
  description,
  action,
  className,
  ...rest
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'phx-empty-state gap-phx-4 flex flex-col items-center justify-center',
        'rounded-phx-md border-phx-border bg-phx-surface border border-dashed',
        'px-phx-6 py-phx-9 text-center',
        className
      )}
      {...rest}
    >
      <span
        aria-hidden
        className="bg-phx-primary-tint text-phx-primary inline-flex h-10 w-10 items-center justify-center rounded-full"
      >
        <Icon name={icon} size={20} />
      </span>
      <div className="text-phx-md text-phx-text font-semibold">{title}</div>
      {description ? (
        <div className="text-phx-sm text-phx-text-muted max-w-md leading-relaxed">
          {description}
        </div>
      ) : null}
      {action ? (
        <Button variant={action.variant ?? 'primary'} size="md" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
